import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
	EndorsementActionRequest,
	EndorsementCreateRequest,
	EndorsementInvalidateRequest,
	EndorsementResponse,
	EndorsementUpdateRequest,
} from '../types/endorsements';
import { Policy2, PolicyPriceChangeResponse } from '../types/policy';
import { getApiUrl } from '../util/util';
import { get, patch, post } from './http';

interface EndorsementsApi {
	getEndorsementsForPolicy(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<EndorsementResponse[]>>;
	createEndorsement(
		authorizationToken: string,
		policyLocator: string,
		params: EndorsementCreateRequest,
	): Promise<AxiosResponse<EndorsementResponse>>;
	getEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
	): Promise<AxiosResponse<EndorsementResponse>>;
	updateEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
		params: EndorsementActionRequest,
	): Promise<AxiosResponse<EndorsementResponse>>;
	discardEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
	): Promise<AxiosResponse<EndorsementResponse>>;
	previewEndorsementPrice(
		authorizationToken: string,
		endorsementLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyPriceChangeResponse>>;
	acceptEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
	): Promise<AxiosResponse<EndorsementResponse>>;
	issueEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
	): Promise<AxiosResponse<EndorsementResponse>>;
	quoteEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
	): Promise<AxiosResponse<EndorsementResponse>>;
	invalidateEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
		actionReq: EndorsementInvalidateRequest,
	): Promise<AxiosResponse<EndorsementResponse>>;
	getPolicyIfEndorsementIssued(
		authorizationToken: string,
		endorsementLocator: string,
	): Promise<AxiosResponse<Policy2>>;
}

export default {
	getEndorsementsForPolicy(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policies/${policyLocator}/endorsements`,
			authorizationToken,
			config,
		);
	},
	getEndorsement(authorizationToken: string, endorsementLocator: string) {
		return get(
			`${getApiUrl()}/endorsements/${endorsementLocator}`,
			authorizationToken,
		);
	},
	createEndorsement(
		authorizationToken: string,
		policyLocator: string,
		params: EndorsementCreateRequest,
	) {
		return post(
			`${getApiUrl()}/policies/${policyLocator}/endorsements`,
			authorizationToken,
			params,
		);
	},
	updateEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
		params: EndorsementUpdateRequest,
	) {
		return patch(
			`${getApiUrl()}/endorsements/${endorsementLocator}/update`,
			authorizationToken,
			params,
		);
	},
	discardEndorsement(authorizationToken: string, endorsementLocator: string) {
		return patch(
			`${getApiUrl()}/endorsements/${endorsementLocator}/discard`,
			authorizationToken,
			{},
		);
	},
	previewEndorsementPrice(
		authorizationToken: string,
		endorsementLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/endorsements/${endorsementLocator}/price`,
			authorizationToken,
			config,
		);
	},
	acceptEndorsement(authorizationToken: string, endorsementLocator: string) {
		return patch(
			`${getApiUrl()}/endorsements/${endorsementLocator}/accept`,
			authorizationToken,
			{ conflictHandling: 'invalidate' },
		);
	},
	issueEndorsement(authorizationToken: string, endorsementLocator: string) {
		return patch(
			`${getApiUrl()}/endorsements/${endorsementLocator}/issue`,
			authorizationToken,
			{ conflictHandling: 'invalidate' },
		);
	},
	quoteEndorsement(authorizationToken: string, endorsementLocator: string) {
		return patch(
			`${getApiUrl()}/endorsements/${endorsementLocator}/quote`,
			authorizationToken,
			{ conflictHandling: 'invalidate' },
		);
	},
	invalidateEndorsement(
		authorizationToken: string,
		endorsementLocator: string,
		invalidateRequest: EndorsementInvalidateRequest,
	) {
		return patch(
			`${getApiUrl()}/endorsements/${endorsementLocator}/invalidate`,
			authorizationToken,
			invalidateRequest,
		);
	},
	getPolicyIfEndorsementIssued(
		authorizationToken: string,
		endorsementLocator: string,
	) {
		return get(
			`${getApiUrl()}/endorsements/${endorsementLocator}/policyWhenIssued`,
			authorizationToken,
		);
	},
} as EndorsementsApi;
